import type { AxiosError } from 'axios';
import { useAuthEvents } from '@gov-nx/core/events/Auth/useAuthEvents';
import { useMessageEvents } from '@gov-nx/core/events/Message/useMessageEvents';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import type { UncheckedKey } from '@gov-nx/core/service/translations/translation.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import type { Optional } from '@gov-nx/core/types/helpers.types';
import type { POResponseError, ResponseError } from './errors.types';
import { runOnUnauthorizedHandler } from './onUnauthorizedRegistry';
import {
	getPoErrors,
	isPoResponseError,
	isSinglePoError,
	useUnauthorizedResponse,
} from './utils';

export const usePoResponseError = (config?: {
	doNotShowToastError?: boolean;
}) => {
	const { doNotShowToastError = false } = config ?? {};
	const { toastMessageError } = useMessageEvents();
	const { t } = useLocale(LocalizeNameSpaceTypes.Global);
	const { userLogout } = useAuthEvents();
	const { isUnauthorizedResponse } = useUnauthorizedResponse();

	const defaultErrorMessage = () => t('notification.error.default');

	const readable = (error: POResponseError['chyby'][number]): string => {
		if (error.kod && error.kod === 'NENALEZENO') {
			return t('notification.error.NENALEZENO');
		}
		if (error.extKod && error.extKod === 'B_JEDEN_SOUBOR') {
			return t('notification.error.B_JEDEN_SOUBOR');
		}
		if (error.extKod && error.extKod === 'ISZR_EGON_CHYBA') {
			return t('notification.error.ISZR_EGON_CHYBA');
		}
		if (error.extKod && error.extKod === 'DZ_NELZE_SMAZAT_ARCHIVACE_EXISTS') {
			return t('notification.error.DZ_NELZE_SMAZAT_ARCHIVACE_EXISTS');
		}
		if (error.extPopis) {
			return error.extPopis;
		}
		if (error.popis) {
			return error.popis;
		}

		if (error.extKod || error.kod) {
			const localised = t<UncheckedKey>(
				`notification.error.${error.extKod ?? error.kod}`
			);
			if (localised) {
				return localised;
			}
		}

		return defaultErrorMessage();
	};

	return {
		defaultErrorMessage,
		readableFromUrl: (errorParameter: string): Optional<string> => {
			const codeOrMsg = decodeURIComponent(
				(atob(errorParameter) + '').replace(/\+/g, '%20')
			);
			if (codeOrMsg.indexOf('[') !== -1) {
				const errorPart = codeOrMsg.substr(0, codeOrMsg.indexOf('['));

				return (
					t<UncheckedKey>('notification.error.' + errorPart + '_') ?? undefined
				);
			}

			return t<UncheckedKey>('notification.error.' + codeOrMsg) ?? undefined;
		},
		handleError: (error: AxiosError<ResponseError>) => {
			if (isUnauthorizedResponse(error)) {
				runOnUnauthorizedHandler();
				userLogout();
				return;
			}

			// TODO: find a better way how not to show toast
			if (doNotShowToastError) {
				return;
			}

			let messages = [defaultErrorMessage()];
			if (isPoResponseError(error)) {
				messages = getPoErrors(error)?.map(readable) ?? [];
			} else if (isSinglePoError(error)) {
				messages = error.response?.data
					? [readable(error.response.data)]
					: [defaultErrorMessage()];
			}
			messages.forEach((m) => {
				toastMessageError(m, {
					time: 0,
				});
			});
		},
	};
};
